import { fileStore } from '@/features/chats/model/draft/fileStore';
import { mediaStorage } from '@/features/indexedDB';
import { resumeMediaUpload, cancelMediaUpload as sdkCancelUpload } from '@/infrastructure/sdk';
import { UPLOAD_STATUS } from '@/shared/constants';
import { logger } from '@/shared/utils';
import { syncInviteStatusOnSend } from './messageDispatchers';
import { MessageService } from './messageService';
import { clearMediaUploadProgress, MEDIA_TYPE_MAP, updateMediaUploadStatus } from './queueUtils';

// Track hanging promises so we can force-reject them on cancellation
const activeUploadPromises = new Map();

// ─── File Sanitization ────────────────────────────────────────────────────────

const UNSUPPORTED_FILE_PROPS = ['fileDetails', 'caption', 'mentionedUsersIds'];

const sanitizeFile = fileObject => {
   for (const prop of UNSUPPORTED_FILE_PROPS) {
      try {
         delete fileObject[prop];
      } catch {
         /* non-configurable */
      }
   }
};

const resolveMediaType = (messageType, { msgBody } = {}) => {
   const typeMapper = MEDIA_TYPE_MAP[messageType];
   return typeMapper ? typeMapper(msgBody?.media?.audioType) : messageType;
};

// ─── Upload Executors ─────────────────────────────────────────────────────────

const uploadFreshFile = (messageObject, recipientJid, messageType, { fileOptions } = {}) => {
   const { msgId, msgBody } = messageObject;
   const file = fileOptions?.file ?? fileStore.getFile(msgId);
   if (!file) {
      logger.error(`[MediaUpload] File not found for fresh upload: ${msgId}`);
      throw new Error(`[MediaUpload] File not found: ${msgId}`);
   }

   logger.info(`[MediaUpload] uploadFreshFile initiating SDK call for msgId: ${msgId}`);
   sanitizeFile(file);
   const fileMessageParams = {
      file,
      msgId,
      mediaGroupId: msgBody?.media?.mediaGroupId ?? '',
      caption: msgBody?.media?.caption ?? fileOptions?.fileDetails?.caption ?? '',
      ...msgBody?.media,
      thumbImage: msgBody?.media?.thumb_image,
      fileSize: msgBody?.media?.file_size,
   };
   return new Promise((resolve, reject) => {
      activeUploadPromises.set(msgId, { resolve, reject });
      MessageService.sendMedia(
         {
            toJid: recipientJid,
            messageType: resolveMediaType(messageType, { msgBody }),
            fileMessageParams,
            mentionedUsersIds: msgBody?.mentionedUsersIds ?? [],
            replyMessageId: msgBody?.replyTo ?? '',
         },
         () => {},
         res => {
            activeUploadPromises.delete(msgId);
            resolve(res);
         },
         err => {
            activeUploadPromises.delete(msgId);
            logger.error(`[MediaUpload] Upload failed (${msgId}):`, err);
            reject(err);
         },
      );
   });
};

const uploadResumedFile = msgId =>
   new Promise((resolve, reject) => {
      logger.info(`[MediaUpload] uploadResumedFile initiating SDK call for msgId: ${msgId}`);
      activeUploadPromises.set(msgId, { resolve, reject });
      resumeMediaUpload(
         msgId,
         () => {},
         res => {
            activeUploadPromises.delete(msgId);
            resolve(res);
         },
         err => {
            activeUploadPromises.delete(msgId);
            logger.error(`[MediaUpload] Resume failed (${msgId}):`, err);
            reject(err);
         },
      );
   });

const finalizeUpload = async (response, msgId, chatId, messageType, isResume) => {
   const isSuccess = response?.statusCode === 200 || !!response?.msgId;

   if (isSuccess) {
      if (messageType === 'image' && !isResume) {
         try {
            const file = fileStore.getFile(msgId);
            const fileSave = false;
            if (file && fileSave) await mediaStorage.save(msgId, file);
         } catch (err) {
            logger.error(`[MediaUpload] Cache failed (${msgId}):`, err);
         }
      }
      updateMediaUploadStatus({ chatId, msgId, is_uploading: UPLOAD_STATUS.COMPLETED });
      fileStore.removeFile(msgId);
   } else {
      updateMediaUploadStatus({ chatId, msgId, is_uploading: UPLOAD_STATUS.FAILED });
   }
};

// ─── Public API ───────────────────────────────────────────────────────────────

export const dispatchMediaMessage = async (
   { msgId, msgBody, ...rest },
   chatId,
   recipientJid,
   uploadOptions,
   isResume,
   messageType,
) => {
   const currentStatus = msgBody?.media?.is_uploading;
   logger.info(
      `[MediaUpload] dispatchMediaMessage called for msgId: ${msgId}, currentStatus: ${currentStatus}, isResume: ${isResume}`,
   );

   if (currentStatus === UPLOAD_STATUS.IN_PROGRESS || currentStatus === UPLOAD_STATUS.COMPLETED) {
      logger.info(`[MediaUpload] dispatchMediaMessage skipping msgId: ${msgId} due to status: ${currentStatus}`);
      return;
   }

   updateMediaUploadStatus({ chatId, msgId, is_uploading: UPLOAD_STATUS.IN_PROGRESS });

   try {
      const messageObject = { msgId, msgBody, ...rest };
      let response;
      let finalIsResume = isResume;

      if (isResume) {
         try {
            response = await uploadResumedFile(msgId);
         } catch (resumeErr) {
            if (resumeErr?.message === 'CANCELLED') {
               logger.info(`[MediaUpload] Resume was cancelled for msgId: ${msgId}, aborting fallback`);
               throw resumeErr;
            }
            logger.warn(`[MediaUpload] Resume failed, falling back to fresh upload for msgId: ${msgId}`, resumeErr);
            clearMediaUploadProgress(msgId);
            response = await uploadFreshFile(messageObject, recipientJid, messageType, uploadOptions);
            finalIsResume = false;
         }
      } else {
         clearMediaUploadProgress(msgId);
         response = await uploadFreshFile(messageObject, recipientJid, messageType, uploadOptions);
      }

      await finalizeUpload(response, msgId, chatId, messageType, finalIsResume);
      const isSuccess = response?.statusCode === 200;
      if (isSuccess) syncInviteStatusOnSend(recipientJid);
   } catch (err) {
      if (err?.message === 'CANCELLED') {
         logger.info(`[MediaUpload] Upload forcefully cancelled for msgId: ${msgId}`);
      } else {
         logger.error(`[MediaUpload] Media failed (${msgId}):`, err);
      }
      throw err;
   } finally {
      activeUploadPromises.delete(msgId);
   }
};

export const cancelUpload = async (msgId, chatId) => {
   try {
      logger.info(`[MediaUpload] cancelUpload initiating SDK call for msgId: ${msgId}`);
      await sdkCancelUpload(msgId);

      // Forcefully reject hanging promises so the queue doesn't stay locked forever
      if (activeUploadPromises.has(msgId)) {
         logger.info(`[MediaUpload] Forcefully rejecting hanging promise for msgId: ${msgId}`);
         activeUploadPromises.get(msgId).reject(new Error('CANCELLED'));
         activeUploadPromises.delete(msgId);
      }

      updateMediaUploadStatus({ chatId, msgId, is_uploading: UPLOAD_STATUS.FAILED });
      logger.info(`[MediaUpload] cancelUpload completed for msgId: ${msgId}`);
   } catch (err) {
      logger.error(`[MediaUpload] Cancel failed (${msgId}):`, err);
   }
};
